import { useMemo } from 'react';
import Highcharts from 'highcharts';
import { EventRow, TimelineChartOrientation } from '@opengov/stories-shared/types';
import { getVerticalTimelineHeight } from '@opengov/stories-shared/utils/progressTracker';
import { capitalize, dataLabelSideForIndex, getStatusColor, getStatusTitle, getTooltipPositioner, getTooltipFormatter, SHARED_TIMELINE_CHART_CONFIG } from '@opengov/stories-shared/constants/timelineChart';
import { useProgressTrackerSettingsContext } from '../../StoriesSettings/ProgressTrackerSettings/context/useProgressTrackerSettingsContext';

interface UseTimelineOptionsProps {
	events: EventRow[];
	orientation?: TimelineChartOrientation;
}

export const useTimelineOptions = ({ events, orientation }: UseTimelineOptionsProps) => {
	const { progressTrackerSettings: ProgressTrackerSettings } = useProgressTrackerSettingsContext();

	const timelineOptions = useMemo((): Highcharts.Options => {
		// Timeline data
		const timelineData = events.map((event, index) => ({
			x: index,
			y: 1,
			name: event.title || "",
			description: event.description,
			color: getStatusColor(event.status.id, ProgressTrackerSettings),
			status: event.status,
			... {
				dataLabels: dataLabelSideForIndex(event.status.id, ProgressTrackerSettings, orientation ?? TimelineChartOrientation.VERTICAL)
			}
		}));

		// Legend data (unique statuses)
		const uniqueStatuses = Array.from(new Set(events.map(e => e.status.id)));
		const legendItems = uniqueStatuses.map(statusId => ({
			name: capitalize(getStatusTitle(statusId, ProgressTrackerSettings)),
			color: getStatusColor(statusId, ProgressTrackerSettings)
		}));

		//Base chart config merged safely
		const base = Highcharts.merge(true, {}, SHARED_TIMELINE_CHART_CONFIG, {
			title: { text: "" },
			accessibility: {
				enabled: false
			},
			chart: {
				type: "timeline",
				...(orientation === TimelineChartOrientation.VERTICAL
					? { height: getVerticalTimelineHeight(events), inverted: true }
					: { inverted: false, height: 200 }),
				events: {
					load: function () {
						this.update({ accessibility: { enabled: true } }, false);
						this.redraw();
					}
				}
			},
			tooltip:
				orientation === TimelineChartOrientation.VERTICAL
					? { enabled: false }
					: {
						enabled: true,
						useHTML: true,
						backgroundColor: "transparent",
						borderWidth: 0,
						shadow: false,
						formatter: getTooltipFormatter(),
						positioner: getTooltipPositioner()
					},
			legend: {
				enabled: true, // set to true if you want the custom legend series
				align: "center",
				verticalAlign: "bottom",
				layout: "horizontal",
				useHTML: true,
				symbolHeight: 0,
				symbolWidth: 0,
				symbolRadius: 0,
				itemStyle: {
					fontSize: "16px",
					cursor: "default",
					pointerEvents: "none"
				},
				labelFormatter: function () {
					const series = this as Highcharts.Series;
					const color = series.color || "#000000";
					return `
						<div style="display: flex; align-items: center; gap: 8px; pointer-events: none; cursor: default;">
							<div style="
								width: 16px;
								height: 16px;
								border-radius: 50%;
								background-color: ${color};
								border: 2px solid #fff;
								flex-shrink: 0;
								pointer-events: none;
							"></div>
							<span style="pointer-events: none; cursor: default;">${series.name}</span>
						</div>
					`;
				}
			},
			series: [
				{
					// main timeline
					...(SHARED_TIMELINE_CHART_CONFIG.series?.[0] || {}),
					type: "timeline",
					showInLegend: false,
					dataLabels:
						orientation === TimelineChartOrientation.VERTICAL
							? Highcharts.merge(
								(SHARED_TIMELINE_CHART_CONFIG.series?.[0] || {}).dataLabels || {},
								{
									enabled: true,
									useHTML: true,
									format: `<div style="max-width:300px;">
										<div style="font-size:14px; font-weight:bold;">{point.name}</div>
										<div style="font-size:14px; font-weight:normal; white-space:normal;">{point.description}</div>
										<div style="color:{point.color}; font-size:12px; font-weight:normal;">{point.status.title}</div>
									</div>`
								}
							)
							: Highcharts.merge(
								(SHARED_TIMELINE_CHART_CONFIG.series?.[0] || {}).dataLabels || {},
								{
									enabled: true,
									useHTML: true,
									format: `<div style="text-align:center; max-width:150px; overflow:hidden; white-space:nowrap; text-overflow:ellipsis;">
										<div style="font-size:16px; font-weight:bold; overflow:hidden; text-overflow:ellipsis;">{point.name}</div>
										<div style="color:{point.color}; font-size:12px; font-weight:normal;">{point.status.title}</div>
									</div>`,
									style: { fontSize: "16px", fontWeight: "bold" }
								}
							),
					data: timelineData
				} as Highcharts.SeriesTimelineOptions,

				...(legendItems.map(item => ({
					type: "timeline" as const,
					name: item.name,
					color: item.color,
					showInLegend: true,
					visible: false,
					enableMouseTracking: false,
					data: [{ x: -1e6, y: -1e6, name: item.name, color: item.color }],
					colorByPoint: false,
					marker: {
						symbol: "circle",
						radius: 8,
						lineWidth: 2,
						lineColor: "#fff",
						fillColor: item.color,
						states: { hover: { lineWidthPlus: 0 } }
					}
				})))
			]
		} as Highcharts.Options);
		return base as unknown as Highcharts.Options;
	}, [events, orientation, ProgressTrackerSettings]);

	return timelineOptions;
}; 